import { useEffect, useState, type ReactNode } from 'react'
import { api } from '../../lib/api.js'
import { BrandMark } from './BrandMark.js'
import '../../styles/signed-out.css'

export interface SignedOutLayoutProps {
  title: string
  /** A line under the heading — the sign-in screen's "Signing in to <host>". */
  subtitle?: ReactNode
  children: ReactNode
}

interface Brand {
  version: string | null
  name: string | null
  logoUrl: string | null
}

/**
 * The server version and the organisation's logo (Settings → Instance), from
 * the unauthenticated /api/health. Plain state rather than useQuery: these
 * screens mount outside the QueryClientProvider in their tests. Until it
 * answers (or if it never does) the page shows the Loop mark and no version.
 */
function useBrand(): Brand {
  const [brand, setBrand] = useState<Brand>({ version: null, name: null, logoUrl: null })
  useEffect(() => {
    let cancelled = false
    api
      .health()
      .then((health) => {
        if (!cancelled) setBrand({ version: health.version, name: health.organisation?.name ?? null, logoUrl: health.organisation?.logoUrl ?? null })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])
  return brand
}

/**
 * Every signed-out screen (sign in, accept invite, reset password): a canvas
 * with a soft accent glow, the organisation's logo (the Loop mark when none is
 * set), then a 400px raised card holding the heading and the form, and a
 * quiet footer naming LoopCMS and the server version.
 */
export function SignedOutLayout({ title, subtitle, children }: SignedOutLayoutProps) {
  const brand = useBrand()
  return (
    <main
      className="screen stack lc-signed-out"
      style={{ alignItems: 'center', justifyContent: 'center', padding: 'var(--space-8) var(--space-4)' }}
    >
      <div className="stack" style={{ width: 'min(400px, 100%)', gap: 'var(--space-6)', alignItems: 'center' }}>
        {brand.logoUrl ? (
          <div className="lc-signed-out-logo">
            <img src={brand.logoUrl} alt={brand.name ?? 'Organisation logo'} />
          </div>
        ) : (
          <BrandMark animate size={40} />
        )}
        <div className="lc-signed-out-card">
          <div className="stack" style={{ gap: 'var(--space-1)' }}>
            <h1 style={{ fontSize: 21, letterSpacing: '-0.02em', margin: 0 }}>{title}</h1>
            {subtitle}
          </div>
          {children}
        </div>
        <p className="row text-muted" style={{ gap: 6, margin: 0, fontSize: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span>Powered by LoopCMS</span>
          <span aria-hidden>·</span>
          <span>{brand.version ? `v${brand.version} · self-hosted` : 'self-hosted'}</span>
        </p>
      </div>
    </main>
  )
}

export const PASSWORD_TOO_SHORT = 'Use at least eight characters.'
export const PASSWORDS_DIFFER = "Those passwords don't match."
